import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  Button,
  Badge
} from '@mui/material';
import { Visibility, ExpandMore } from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import contribuenteApi from '../../api/contribuenteApi';
import documentoApi from '../../api/documentoApi';
import scadenzaApi from '../../api/scadenzaApi';
import filesApi from '../../api/filesApi';
import ConfirmDialog from '../../components/ConfirmDeleteModal';
import KpiCard from '../../components/KpiCard';
import PaymentIcon from '@mui/icons-material/Payment';
import { scadenzaStatusMap } from '../../utils/scadenzaStatusMapper';
import { formatDate } from '../../utils/dateFormatter';

const pageSize = 5;
const truncateText = (text, maxLength = 80) => {
  if (!text) return '';
  if (text.length <= maxLength) return text;

  return text.substring(0, maxLength).trim() + '...';
};
export default function DashboardContribuenteMobile() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [contribuente, setContribuente] = useState(null);
  const [documenti, setDocumenti] = useState([]);
  const [scadenzeProssimeEntrata, setScadenzeProssimeEntrata] = useState([]);
  const [scadenzeScaduteEntrata, setScadenzeScaduteEntrata] = useState([]);
  const [scadenzeProssime, setScadenzeProssime] = useState([]);
  const [scadenzeScadute, setScadenzeScadute] = useState([]);

  const [stats, setStats] = useState({ prossime: 0, scadute: 0 });

  const [openDelete, setOpenDelete] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deleteType, setDeleteType] = useState('');

  useEffect(() => {
    fetchContribuente();
    fetchDocumenti();
    fetchScadenze();
  }, [id]);

  const fetchContribuente = async () => {
    const res = await contribuenteApi.get(id);
    setContribuente(res.data);
  };

  const fetchDocumenti = async () => {
    const res = await documentoApi.listbycontribuente(id, { page: 0, size: 100 });
    setDocumenti(res.data.content || []);
  };

  const fetchScadenze = async () => {
    const res = await scadenzaApi.listbycontribuente(id, { page: 0, size: 1000 });
    const all = res.data.content || [];

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const prossime = [];
    const scadute = [];

    const prossimeentrata = [];
    const scaduteentrata = [];

    all.forEach(s => {
      const dataScad = new Date(s.dataScadenza);
      dataScad.setHours(0, 0, 0, 0);

      const diff = (dataScad - today) / (1000 * 60 * 60 * 24);

      if (diff < 0 && s.stato !== 'pagata' && s.direzione!=='ENTRATA') scadute.push(s);
      else if (diff >= 0 && diff <= 7 && s.stato !== 'pagata' && s.direzione!=='ENTRATA') prossime.push(s);

      if (diff < 0 && s.stato !== 'pagata' && s.direzione==='ENTRATA') scaduteentrata.push(s);
      else if (diff >= 0 && diff <= 7 && s.stato !== 'pagata' && s.direzione==='ENTRATA') prossimeentrata.push(s);
    });

    setScadenzeProssime(prossime);
    setScadenzeScadute(scadute);

    setScadenzeProssimeEntrata(prossimeentrata);
    setScadenzeScaduteEntrata(scaduteentrata);

    setStats({
      prossime: prossime.length,
      scadute: scadute.length,
      prossimeentrata: prossimeentrata.length,
      scaduteentrata: scaduteentrata.length
    });
  };

  const openPdf = async (filename) => {
    const response = await filesApi.getfile(filename);
    const url = window.URL.createObjectURL(
      new Blob([response.data], { type: 'application/pdf' })
    );
    window.open(url, '_blank');
    window.URL.revokeObjectURL(url);
  };

  const confirmDelete = (id, type) => {
    setSelectedId(id);
    setDeleteType(type);
    setOpenDelete(true);
  };

  const handleDelete = async () => {
    if (deleteType === 'doc') {
      await documentoApi.delete(selectedId);
      fetchDocumenti();
    }
    if (deleteType === 'scad') {
      await scadenzaApi.delete(selectedId);
      fetchScadenze();
    }

    setOpenDelete(false);
    setSelectedId(null);
    setDeleteType('');
  };

  if (!contribuente) return <Box p={2}>Loading...</Box>;

  return (
    <Box sx={{ pb: 4 }}>

      {/* HEADER */}
      <Box sx={{ px: 2, pt: 2 }}>
        <Typography variant="h6" fontWeight={600}>
          👤 {contribuente.nome} {contribuente.cognome}
        </Typography>
        <Typography variant="caption">
          CF: {contribuente.codiceFiscale}
        </Typography>
      </Box>

      {/* KPI */}
      <Stack direction="row" spacing={1} sx={{ px: 2, mt: 2, overflowX: 'auto' }}>
        <KpiCard title="Totale Documenti" value={documenti.length} />
        <KpiCard title="Prossime (uscita)" value={stats.prossime} color="warning" />
        <KpiCard title="Scadute (uscita)" value={stats.scadute} color="error" />
        <KpiCard title="Prossime (entrata)" value={stats.prossimeentrata} color="info" />
        <KpiCard title="Scadute (entrata)" value={stats.scaduteentrata} color="error" />
      </Stack>

      {/* DOCUMENTI */}
      <Typography sx={{ mt: 3, px: 2, fontWeight: 600 }}>
        Documenti
      </Typography>

      <Stack spacing={1} sx={{ px: 2 }}>
        {documenti.map(d => (
          <Accordion key={d.id} disableGutters>
            <AccordionSummary
              expandIcon={<ExpandMore />}
              sx={{
                '& .MuiAccordionSummary-content': {
                  width: '100%',
                  margin: '12px 0',
                }
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ width: '100%' }}
              >
                {/* TESTO */}
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" noWrap>
                    {d.tipo}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{ wordBreak: 'break-word' }}
                  >
                    {truncateText(d.descrizione, 80)}
                  </Typography>
                </Box>

                {/* BADGE */}
                {d.rateSaltate > 0 && (
                  <Badge
                    color="error"
                    badgeContent={d.rateSaltate}
                    sx={{
                      mr: 1,
                      '& .MuiBadge-badge': {
                        position: 'static',
                        transform: 'none',
                      }
                    }}
                  />
                )}
              </Stack>
            </AccordionSummary>


            <AccordionDetails>
            {d.descrizione && d.descrizione.length > 80 && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                {d.descrizione}
              </Typography>
            )}
              <Typography variant="caption">
                {formatDate(d.dataCreazione)}
              </Typography>

              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Button size="small" onClick={() => navigate(`/documenti/${d.id}`)}>
                  Dettaglio
                </Button>

                <Button size="small" onClick={() => navigate(`/documenti/${d.id}/edit`)}>
                  Modifica
                </Button>

                <Button
                  size="small"
                  color="error"
                  onClick={() => confirmDelete(d.id, 'doc')}
                >
                  Elimina
                </Button>

                {d.pdfPath && (
                  <Button size="small" onClick={() => openPdf(d.pdfPath)}>
                    PDF
                  </Button>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      {/* SCADUTE */}
      <Typography sx={{ mt: 3, px: 2, color: 'error.main', fontWeight: 600 }}>
        Scadenze prossime (uscita)
      </Typography>

      <Stack spacing={1} sx={{ px: 2 }}>
        {scadenzeProssime.slice(0, pageSize).map(s => (
          <Card key={s.id} sx={{ borderLeft: '4px solid red' }}>
            <CardContent>
              <Typography fontWeight={600}>
                {s.tipo}
              </Typography>

              <Typography variant="caption">
                {formatDate(s.dataScadenza)}
              </Typography>

              <Typography fontWeight="bold">
                € {s.importo}
              </Typography>

              <Chip
                size="small"
                label={scadenzaStatusMap[s.direzione!="ENTRATA"?s.stato:s.stato!="pagata"?"da_incassare":s.stato]?.label || s.stato}
                color={scadenzaStatusMap[s.direzione!="ENTRATA"?s.stato:s.stato!="pagata"?"da_incassare":s.stato]?.color || 'default'}
                sx={{ mt: 1 }}
              />

              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" onClick={() => navigate(`/scadenze/${s.id}`)}>
                  Dettaglio
                </Button>

                {s.stato !== 'pagata' && (
                  <Button
                    size="small"
                    startIcon={<PaymentIcon />}
                      onClick={async () => {
                        try {
                          // chiama rotta interna per marcare pagata senza email
                          await scadenzaApi.setpagatamanuale(s.id);
                          fetchScadenze(scadPage);
                        } catch (err) {
                          console.error('Errore marcatura manuale pagamento:', err);
                        }
                      }}
                  >
                    Paga
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>

      {/* PROSSIME */}
      <Typography sx={{ mt: 3, px: 2, color: 'warning.main', fontWeight: 600 }}>
        Scadenze scadute (uscita)
      </Typography>

      <Stack spacing={1} sx={{ px: 2 }}>
        {scadenzeScadute.slice(0, pageSize).map(s => (
          <Card key={s.id}>
            <CardContent>
              <Typography>{s.tipo}</Typography>
              <Typography variant="caption">
                {formatDate(s.dataScadenza)}
              </Typography>

              <Typography fontWeight="bold">
                € {s.importo}
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                <Button size="small" onClick={() => navigate(`/scadenze/${s.id}`)}>
                  Dettaglio
                </Button>
              <Chip
                size="small"
                label={scadenzaStatusMap[s.direzione!="ENTRATA"?s.stato:s.stato!="pagata"?"da_incassare":s.stato]?.label || s.stato}
                color={scadenzaStatusMap[s.direzione!="ENTRATA"?s.stato:s.stato!="pagata"?"da_incassare":s.stato]?.color || 'default'}
                sx={{ mt: 1 }}
              />
                {s.stato !== 'pagata' && (
                  <Button
                    size="small"
                    startIcon={<PaymentIcon />}
                      onClick={async () => {
                        try {
                          // chiama rotta interna per marcare pagata senza email
                          await scadenzaApi.setpagatamanuale(s.id);
                          fetchScadenze(scadPage);
                        } catch (err) {
                          console.error('Errore marcatura manuale pagamento:', err);
                        }
                      }}
                  >
                    Paga
                  </Button>
                )}
              </Stack>
            </CardContent>
          </Card>
        ))}
      </Stack>


      {/* SCADUTE */}
            <Typography sx={{ mt: 3, px: 2, color: 'error.main', fontWeight: 600 }}>
              Scadenze prossime (entrata)
            </Typography>

            <Stack spacing={1} sx={{ px: 2 }}>
              {scadenzeProssimeEntrata.slice(0, pageSize).map(s => (
                <Card key={s.id} sx={{ borderLeft: '4px solid red' }}>
                  <CardContent>
                    <Typography fontWeight={600}>
                      {s.tipo}
                    </Typography>

                    <Typography variant="caption">
                      {formatDate(s.dataScadenza)}
                    </Typography>

                    <Typography fontWeight="bold">
                      € {s.importo}
                    </Typography>

                    <Chip
                      size="small"
                      label={scadenzaStatusMap[s.direzione!="ENTRATA"?s.stato:s.stato!="pagata"?"da_incassare":s.stato]?.label || s.stato}
                      color={scadenzaStatusMap[s.direzione!="ENTRATA"?s.stato:s.stato!="pagata"?"da_incassare":s.stato]?.color || 'default'}
                      sx={{ mt: 1 }}
                    />

                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button size="small" onClick={() => navigate(`/scadenze/${s.id}`)}>
                        Dettaglio
                      </Button>

                      {s.stato !== 'pagata' && (
                        <Button
                          size="small"
                          startIcon={<PaymentIcon />}
                          onClick={async () => {
                            try {
                              // chiama rotta interna per marcare pagata senza email
                              await scadenzaApi.setpagatamanuale(s.id);
                              fetchScadenze(scadPage);
                            } catch (err) {
                              console.error('Errore marcatura manuale pagamento:', err);
                            }
                          }}
                        >
                          Paga
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>

            {/* PROSSIME */}
            <Typography sx={{ mt: 3, px: 2, color: 'warning.main', fontWeight: 600 }}>
              Scadenze scadute (entrata)
            </Typography>

            <Stack spacing={1} sx={{ px: 2 }}>
              {scadenzeScaduteEntrata.slice(0, pageSize).map(s => (
                <Card key={s.id}>
                  <CardContent>
                    <Typography>{s.tipo}</Typography>
                    <Typography variant="caption">
                      {formatDate(s.dataScadenza)}
                    </Typography>

                    <Typography fontWeight="bold">
                      € {s.importo}
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                      <Button size="small" onClick={() => navigate(`/scadenze/${s.id}`)}>
                        Dettaglio
                      </Button>
                    <Chip
                      size="small"
                      label={scadenzaStatusMap[s.direzione!="ENTRATA"?s.stato:s.stato!="pagata"?"da_incassare":s.stato]?.label || s.stato}
                      color={scadenzaStatusMap[s.direzione!="ENTRATA"?s.stato:s.stato!="pagata"?"da_incassare":s.stato]?.color || 'default'}
                      sx={{ mt: 1 }}
                    />
                      {s.stato !== 'pagata' && (
                        <Button
                          size="small"
                          startIcon={<PaymentIcon />}
                          onClick={async () => {
                            try {
                              // chiama rotta interna per marcare pagata senza email
                              await scadenzaApi.setpagatamanuale(s.id);
                              fetchScadenze(scadPage);
                            } catch (err) {
                              console.error('Errore marcatura manuale pagamento:', err);
                            }
                          }}
                        >
                          Paga
                        </Button>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              ))}
            </Stack>

      {/* DELETE */}
      <ConfirmDialog
        open={openDelete}
        onCancel={() => setOpenDelete(false)}
        onConfirm={handleDelete}
        title="Conferma Eliminazione"
        message="Sei sicuro di voler eliminare questo elemento?"
      />
    </Box>
  );
}
